import React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@components/atoms/Button';
import { StatusIndicator } from '@components/atoms/StatusIndicator';
import { ActionButtons } from '@components/molecules/ActionButtons';
import { cn } from '@utils/cn';

interface TaskData {
  id: string;
  name: string;
  startTime: string | Date;
  endTime: string | Date;
  budget?: number | string;
  status?: string;
  isEvent?: boolean;
  children?: TaskData[];
  delegateOrganization?: { name: string };
}

interface TaskRowProps {
  task: TaskData;
  statusDisplay: { color: string; label: string };
  formatDate: (date: string | Date) => string;
  showApprovalActions?: boolean;
  showChildren?: boolean;
  loading?: boolean;
  onApprove?: () => void;
  onReject?: () => void;
  onEdit?: () => void;
  onToggleExpand?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onTaskClick?: () => void;
  onDateClick?: (dateValue: string | Date) => void;
  className?: string;
  children?: React.ReactNode;
}

export const TaskRow: React.FC<TaskRowProps> = ({
  task,
  statusDisplay,
  formatDate,
  showApprovalActions = false,
  showChildren = false,
  loading = false,
  onApprove,
  onReject,
  onEdit,
  onToggleExpand,
  onTaskClick,
  onDateClick,
  className = '',
  children,
}) => {
  const hasChildren = task.children && task.children.length > 0;

  return (
    <>
      <tr
        onClick={onTaskClick}
        className={cn(
          'border-none hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 cursor-pointer',
          'odd:bg-white even:bg-gray-50 dark:odd:bg-gray-800 dark:even:bg-gray-750',
          className,
        )}>
        {/* Expand/Collapse Column */}
        <td className="px-4 py-3 w-12">
          {hasChildren && (
            <Button
              variant="tertiary"
              size="md"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                onToggleExpand?.(e);
              }}
              className="w-8 h-8">
              {showChildren ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </Button>
          )}
        </td>

        {/* Task Name */}
        <td className="px-4 py-3">
          <div className="flex items-center space-x-2">
            <span className="text-lg">{task.isEvent ? '📅' : '📋'}</span>
            <span className="text-sm font-medium text-gray-800 dark:text-gray-100">
              {task.name}
            </span>
          </div>
        </td>

        {/* Start Date */}
        <td
          className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300"
          onClick={(e) => {
            if (onDateClick && task.startTime) {
              e.stopPropagation();
              onDateClick(task.startTime);
            }
          }}
          title={onDateClick ? 'Click to navigate calendar to this date' : undefined}
        >
          <span className={onDateClick ? 'cursor-pointer hover:text-blue-600 hover:underline dark:hover:text-blue-400 transition-colors' : ''}>
            {formatDate(task.startTime)}
          </span>
        </td>

        {/* End Date */}
        <td
          className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300"
          onClick={(e) => {
            if (onDateClick && task.endTime) {
              e.stopPropagation();
              onDateClick(task.endTime);
            }
          }}
          title={onDateClick ? 'Click to navigate calendar to this date' : undefined}
        >
          <span className={onDateClick ? 'cursor-pointer hover:text-blue-600 hover:underline dark:hover:text-blue-400 transition-colors' : ''}>
            {formatDate(task.endTime)}
          </span>
        </td>

        {/* Budget */}
        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
          {task.budget || 'Not specified'}
        </td>

        {/* Status */}
        <td className="px-4 py-3">
          <StatusIndicator
            color={statusDisplay.color}
            label={statusDisplay.label}
            subtitle={
              task.delegateOrganization
                ? `(${task.delegateOrganization.name})`
                : undefined
            }
          />
        </td>

        {/* Actions */}
        <td className="px-4 py-3">
          <ActionButtons
            showApprovalActions={showApprovalActions}
            showEditAction={true}
            loading={loading}
            onApprove={onApprove}
            onReject={onReject}
            onEdit={onEdit}
          />
        </td>

        {/* Edit Button */}
        <td className="px-4 py-3">
          {/* This column is for the edit button which is included in ActionButtons */}
        </td>
      </tr>

      {/* Children/Subtasks Row */}
      {hasChildren && showChildren && (
        <tr>
          <td colSpan={8} className="px-0 py-0">
            <div className="bg-gray-25 dark:bg-gray-850 border-l-4 border-blue-500 ml-4 mr-4 rounded-md">
              <div className="p-4">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                  <span className="mr-2">📋</span>
                  Assigned SubTasks
                </h4>
                {children}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};
